import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { User, LoginResult, SessionValidationResult } from '@domain/ports/IAuthGateway';
import type { StoreExtraDependencies } from '../index';
import type { RootState } from '../index';

/**
 * Field-specific Errors
 */
export type FieldErrors = Record<'email' | 'password', string>;

/**
 * Auth State Interface
 */
export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  googleLoading: boolean;
  appleLoading: boolean;
  signInLoading: boolean;
  error: string | null;
  fieldErrors: FieldErrors | null;
  rememberMe: boolean;
  returnUrl: string | null;
  /**
   * Transient flag indicating this is the user's first login session.
   *
   * Set to true after successful signup or first OAuth login.
   * Used to trigger redirect to /welcome page.
   * Reset after navigation away from welcome page.
   *
   * NOTE: This is NOT persisted - it's only valid for the current session.
   * For persisted first-login tracking, see onboardingSlice.hasSeenFirstLoginWelcome
   */
  isFirstLogin: boolean;
  /**
   * Flag indicating the session has expired
   * Story 4.9: Set when token refresh fails
   * Used to show "session expired" message on login page
   */
  sessionExpired: boolean;
  /**
   * Flag indicating session validation is in progress
   * Story 4.9: Set during validateSession thunk execution
   * Used to show loading state in ProtectedRoute
   */
  sessionValidating: boolean;
}

/**
 * Initial State
 */
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  googleLoading: false,
  appleLoading: false,
  signInLoading: false,
  error: null,
  fieldErrors: null,
  rememberMe: false,
  returnUrl: null,
  isFirstLogin: false,
  sessionExpired: false,
  sessionValidating: false,
};

/**
 * Async Thunk: Login with Email
 *
 * Uses injected authGateway from store dependencies
 * Includes remember me functionality
 * Signs out locally (no federated logout) to clear any OAuth sessions
 */
export const loginWithEmail = createAsyncThunk<
  LoginResult,
  { email: string; password: string; rememberMe: boolean },
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/loginWithEmail', async ({ email, password, rememberMe }, { extra, rejectWithValue }) => {
  try {
    // Sign out locally to clear any existing OAuth session without federated logout redirect
    await extra.authGateway.signOutLocally();

    // Set remember me preference
    await extra.authGateway.rememberUser(rememberMe);

    // Perform login
    const result = await extra.authGateway.login(email, password);
    return result;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.unknownError');
  }
});

/**
 * Async Thunk: Login with OAuth Provider
 *
 * Uses injected authGateway for social authentication
 * Signs out any existing session before starting OAuth flow
 */
export const loginWithProvider = createAsyncThunk<
  void,
  { provider: 'google' | 'apple' },
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/loginWithProvider', async ({ provider }, { extra, rejectWithValue }) => {
  try {
    // First, sign out any existing session to avoid UserAlreadyAuthenticatedException
    try {
      await extra.authGateway.logout();
      console.log('[loginWithProvider] Signed out existing session before OAuth');
    } catch (logoutError) {
      // Ignore logout errors - might not be signed in
      console.log('[loginWithProvider] No existing session to sign out');
    }

    // Now attempt OAuth sign-in
    await extra.authGateway.signInWithProvider(provider);
    // Note: This will redirect, so no return value
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.oauthError');
  }
});

/**
 * Async Thunk: Handle OAuth Callback
 *
 * Processes OAuth callback after redirect from Cognito
 * Amplify handles authorization code exchange automatically
 * This thunk retrieves the authenticated user session and tokens
 */
export const handleOAuthCallback = createAsyncThunk<
  LoginResult,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/handleOAuthCallback', async (_, { extra, rejectWithValue }) => {
  try {
    // Get current authenticated user (Amplify handles code exchange)
    const user = await extra.authGateway.getCurrentUser();

    if (!user) {
      throw new Error('No user session found after OAuth callback');
    }

    // Extract token from Amplify session (same pattern as loginWithEmail)
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';
    const refreshToken = session.tokens?.accessToken?.toString();

    return {
      user,
      token,
      refreshToken,
    };
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.oauthError');
  }
});

/**
 * Legacy Async Thunk: Login User (Deprecated)
 * @deprecated Use loginWithEmail instead
 */
export const loginUser = createAsyncThunk<
  LoginResult,
  { email: string; password: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/login', async (credentials, { extra, rejectWithValue }) => {
  try {
    const result = await extra.authGateway.login(credentials.email, credentials.password);
    return result;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.unknownError');
  }
});

/**
 * Async Thunk: Logout User
 *
 * Uses injected authGateway from store dependencies
 */
export const logoutUser = createAsyncThunk<
  void,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/logout', async (_, { extra, rejectWithValue }) => {
  try {
    await extra.authGateway.logout();
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.logout_failed');
  }
});

/**
 * Async Thunk: Sign Out Locally
 *
 * Clears local auth state without triggering Cognito federated logout.
 * Use this when the user has been deleted from Cognito (e.g., after account deletion)
 * and we only need to clear local state without making any Cognito API calls.
 *
 * Story 3.9.b: Account deletion fix - prevents redirect to Cognito error page
 */
export const signOutLocallyAsync = createAsyncThunk<
  void,
  void,
  { extra: StoreExtraDependencies }
>('auth/signOutLocally', async (_, { extra }) => {
  try {
    await extra.authGateway.signOutLocally();
  } catch (error) {
    // Even if signOutLocally fails, we still want to clear local state
    // So we don't reject here - the state will be cleared in the fulfilled case
    console.warn('signOutLocally error (ignored):', error);
  }
});

/**
 * Async Thunk: Refresh Token
 *
 * Uses injected authGateway from store dependencies
 */
export const refreshToken = createAsyncThunk<
  string,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/refreshToken', async (_, { extra, rejectWithValue }) => {
  try {
    const newToken = await extra.authGateway.refreshToken();
    return newToken;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('auth.errors.refresh_failed');
  }
});

/**
 * Async Thunk: Validate Session
 * Story 4.9: Session Expiration Handling
 *
 * Called on app mount to validate current session:
 * 1. Checks if session is valid via authGateway.validateSession()
 * 2. Proactively refreshes if token expires in < 5 hours (handled in gateway)
 * 3. Updates auth state with validated user/token
 * 4. Sets sessionExpired flag if validation fails
 */
export const validateSession = createAsyncThunk<
  SessionValidationResult,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('auth/validateSession', async (_, { extra, rejectWithValue }) => {
  try {
    const result = await extra.authGateway.validateSession();

    if (!result.isValid) {
      return rejectWithValue(result.error || 'Session expired');
    }

    return result;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Session validation failed');
  }
});

/**
 * Auth Slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.fieldErrors = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },
    setReturnUrl: (state, action: PayloadAction<string | null>) => {
      state.returnUrl = action.payload;
    },
    clearReturnUrl: (state) => {
      state.returnUrl = null;
    },
    /**
     * Sets the first login flag
     *
     * Used to trigger redirect to /welcome page after signup or first OAuth login.
     * Should be set to true after successful signup/first OAuth, and reset after
     * the user navigates away from the welcome page.
     */
    setFirstLogin: (state, action: PayloadAction<boolean>) => {
      state.isFirstLogin = action.payload;
    },
    /**
     * Sets the session expired flag
     * Story 4.9: Used when token refresh fails
     */
    setSessionExpired: (state, action: PayloadAction<boolean>) => {
      state.sessionExpired = action.payload;
    },
    /**
     * Clears the session expired flag
     * Story 4.9: Called after displaying message on login page
     */
    clearSessionExpired: (state) => {
      state.sessionExpired = false;
    },
  },
  extraReducers: (builder) => {
    // Login with Email
    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.signInLoading = true;
        state.isLoading = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.signInLoading = false;
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        state.fieldErrors = null;
        // Remember me state is already set by the thunk
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.signInLoading = false;
        state.isLoading = false;
        state.error = action.payload || 'auth.errors.unknownError';
        state.isAuthenticated = false;
      });

    // Login with Provider (Google/Apple)
    builder
      .addCase(loginWithProvider.pending, (state, action) => {
        const provider = action.meta.arg.provider;
        if (provider === 'google') {
          state.googleLoading = true;
        } else if (provider === 'apple') {
          state.appleLoading = true;
        }
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(loginWithProvider.fulfilled, (state, action) => {
        const provider = action.meta.arg.provider;
        if (provider === 'google') {
          state.googleLoading = false;
        } else if (provider === 'apple') {
          state.appleLoading = false;
        }
        // OAuth redirect initiated - actual auth will complete on callback
      })
      .addCase(loginWithProvider.rejected, (state, action) => {
        const provider = action.meta.arg.provider;
        if (provider === 'google') {
          state.googleLoading = false;
        } else if (provider === 'apple') {
          state.appleLoading = false;
        }
        state.error = action.payload || 'auth.errors.oauthError';
      });

    // Handle OAuth Callback
    builder
      .addCase(handleOAuthCallback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(handleOAuthCallback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.googleLoading = false;
        state.appleLoading = false;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(handleOAuthCallback.rejected, (state, action) => {
        state.isLoading = false;
        state.googleLoading = false;
        state.appleLoading = false;
        state.error = action.payload || 'auth.errors.oauthError';
        state.isAuthenticated = false;
      });

    // Legacy Login User (keep for backward compatibility)
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'auth.errors.unknownError';
        state.isAuthenticated = false;
      });

    // Logout User
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'auth.errors.logout_failed';
        // Still clear user on logout failure
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Refresh Token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'auth.errors.refresh_failed';
        // Clear auth state on refresh failure
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Validate Session (Story 4.9)
    builder
      .addCase(validateSession.pending, (state) => {
        state.sessionValidating = true;
        state.error = null;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.sessionValidating = false;
        if (action.payload.user) {
          state.user = action.payload.user;
        }
        if (action.payload.token) {
          state.token = action.payload.token;
        }
        state.isAuthenticated = true;
        state.sessionExpired = false;
        state.error = null;
      })
      .addCase(validateSession.rejected, (state, action) => {
        state.sessionValidating = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.sessionExpired = true;
        state.error = action.payload || 'Session validation failed';
      });

    // Sign Out Locally (Story 3.9.b - Account deletion)
    // Used when user has been deleted from Cognito and we only need to clear local state
    builder
      .addCase(signOutLocallyAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signOutLocallyAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(signOutLocallyAsync.rejected, (state) => {
        // Even on rejection, clear auth state (user is already deleted from Cognito)
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

/**
 * Actions
 */
export const { clearError, setUser, setRememberMe, setReturnUrl, clearReturnUrl, setFirstLogin, setSessionExpired, clearSessionExpired } = authSlice.actions;

/**
 * Selectors
 */
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectError = (state: RootState) => state.auth.error;
export const selectReturnUrl = (state: RootState) => state.auth.returnUrl;
/**
 * Selector for first login flag
 *
 * Returns true if this is the user's first login session (after signup or first OAuth).
 * Used to determine if user should be redirected to /welcome page.
 */
export const selectIsFirstLogin = (state: RootState) => state.auth.isFirstLogin;

/**
 * Selector for session expired flag
 * Story 4.9: Used by LoginPage to display session expired message
 */
export const selectSessionExpired = (state: RootState) => state.auth.sessionExpired;

/**
 * Selector for session validating flag
 * Story 4.9: Used by ProtectedRoute to show loading during validation
 */
export const selectSessionValidating = (state: RootState) => state.auth.sessionValidating;

/**
 * Selector for auth token
 * Story 4.9: Used by ProtectedRoute for local expiry check
 */
export const selectToken = (state: RootState) => state.auth.token;

/**
 * Reducer
 */
export default authSlice.reducer;
